import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Clinic, ClinicWorkingHour, TimeSlotOption } from '../../models/clinic.model';
import { ClinicService } from './clinic.service';

@Injectable({
  providedIn: 'root',
})
export class ClinicScheduleService {
  constructor(private clinicService: ClinicService) {}

  ensureClinicsLoaded(): Observable<Clinic[]> {
    const cached = this.clinicService.getCachedClinics();
    if (cached.length > 0) {
      return of(cached);
    }

    return this.clinicService.getClinic().pipe(
      map((res: any) => (Array.isArray(res?.data) ? res.data : [])),
      catchError(() => of([])),
    );
  }

  getDayName(date: string): string {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long' });
  }

  getDaySchedule(clinic: Clinic | undefined, date: string): ClinicWorkingHour | undefined {
    if (!clinic?.working_hours?.length) return undefined;
    const dayName = this.getDayName(date);
    return clinic.working_hours.find((entry) => entry.day === dayName);
  }

  isClinicOpenOnDate(clinic: Clinic | undefined, date: string): boolean {
    const schedule = this.getDaySchedule(clinic, date);
    return !!(schedule?.open && schedule?.close);
  }

  getScheduleSummary(clinic: Clinic | undefined): string {
    if (!clinic?.working_hours?.length) {
      return 'No schedule configured';
    }

    const openDays = clinic.working_hours.filter((entry) => entry.open && entry.close);
    if (!openDays.length) {
      return 'Closed all week';
    }

    const first = openDays[0];
    const sameHours = openDays.every((entry) => entry.open === first.open && entry.close === first.close);
    if (sameHours && openDays.length >= 5) {
      return `${openDays.length} days · ${this.formatDisplayTime(first.open)} – ${this.formatDisplayTime(first.close)}`;
    }

    return `${openDays.length} open day${openDays.length === 1 ? '' : 's'}`;
  }

  generateTimeSlots(
    clinicId: number | null | undefined,
    date: string,
    options?: { now?: Date },
  ): TimeSlotOption[] {
    const resolvedClinicId = this.resolveClinicId(clinicId);
    const clinic = resolvedClinicId != null
      ? this.clinicService.getCachedClinicById(resolvedClinicId)
      : undefined;
    const schedule = this.getDaySchedule(clinic, date);

    if (!schedule?.open || !schedule?.close) {
      const hasConfiguredHours = !!(clinic?.working_hours?.length);
      if (!clinic || !hasConfiguredHours) {
        return this.generateLegacyFallbackSlots(date, options?.now, resolvedClinicId);
      }
      return [];
    }

    const slotMinutes = this.clinicService.getSlotDurationMinutes(resolvedClinicId);
    const openMinutes = this.parseTimeToMinutes(schedule.open);
    const closeMinutes = this.effectiveCloseMinutes(this.parseTimeToMinutes(schedule.close));
    const now = options?.now ?? new Date();

    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isPastDate = selectedDate < today;
    const isFutureDate = selectedDate > today;
    const isToday = !isPastDate && !isFutureDate;

    const slots: TimeSlotOption[] = [];

    for (let cursor = openMinutes; cursor + slotMinutes <= closeMinutes; cursor += slotMinutes) {
      const slotEndMinutes = cursor + slotMinutes;
      // Store end as 23:59 (not 00:00) so same-day H:i validation still passes.
      const persistedEndMinutes = slotEndMinutes >= 24 * 60 ? 23 * 60 + 59 : slotEndMinutes;
      const start = this.minutesToTime(cursor);
      const end = this.minutesToTime(persistedEndMinutes);

      let disabled = isPastDate;
      if (isToday) {
        const slotEnd = new Date(selectedDate);
        slotEnd.setHours(Math.floor(slotEndMinutes / 60), slotEndMinutes % 60, 0, 0);
        disabled = slotEnd <= now;
      }

      slots.push({
        label: `${this.formatAmPmFromMinutes(cursor)} - ${this.formatAmPmFromMinutes(slotEndMinutes)}`,
        start,
        end,
        disabled,
      });
    }

    return slots;
  }

  getSlotsSourceLabel(clinicId: number | null | undefined, date: string): string {
    const resolvedClinicId = this.resolveClinicId(clinicId);
    const clinic = resolvedClinicId != null
      ? this.clinicService.getCachedClinicById(resolvedClinicId)
      : undefined;
    const schedule = this.getDaySchedule(clinic, date);

    if (schedule?.open && schedule?.close) {
      return `Using ${clinic?.name || 'clinic'} hours · ${this.getSlotDurationLabel(resolvedClinicId)} slots`;
    }

    if (clinic?.working_hours?.length) {
      return `Clinic is closed on ${this.getDayName(date)}`;
    }

    return 'Using default hours (9:00 AM – 9:00 PM) until clinic schedule is configured';
  }

  private getSlotDurationLabel(clinicId: number | null): string {
    const minutes = this.clinicService.getSlotDurationMinutes(clinicId);
    if (minutes === 60) return '1 hour';
    if (minutes % 60 === 0) return `${minutes / 60} hour`;
    return `${minutes} min`;
  }

  private resolveClinicId(clinicId: number | null | undefined): number | null {
    const id = Number(clinicId);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private generateLegacyFallbackSlots(
    date: string,
    now = new Date(),
    clinicId: number | null = null,
  ): TimeSlotOption[] {
    const slotMinutes = this.clinicService.getSlotDurationMinutes(clinicId);
    const openMinutes = 9 * 60;
    const closeMinutes = 21 * 60;

    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isPastDate = selectedDate < today;
    const isFutureDate = selectedDate > today;
    const isToday = !isPastDate && !isFutureDate;

    const slots: TimeSlotOption[] = [];

    for (let cursor = openMinutes; cursor + slotMinutes <= closeMinutes; cursor += slotMinutes) {
      const start = this.minutesToTime(cursor);
      const end = this.minutesToTime(cursor + slotMinutes);

      let disabled = isPastDate;
      if (isToday) {
        const slotEnd = new Date(now);
        slotEnd.setHours(Math.floor((cursor + slotMinutes) / 60), (cursor + slotMinutes) % 60, 0, 0);
        disabled = slotEnd <= now;
      } else if (isFutureDate) {
        disabled = false;
      }

      slots.push({
        label: `${this.formatAmPmFromMinutes(cursor)} - ${this.formatAmPmFromMinutes(cursor + slotMinutes)}`,
        start,
        end,
        disabled,
      });
    }

    return slots;
  }

  formatDisplayTime(time: string): string {
    if (!time) return '';
    return this.formatAmPmFromMinutes(this.parseTimeToMinutes(time));
  }

  private parseTimeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  }

  /**
   * Close times like 23:59 mean "through end of day" for 24h clinics.
   * Without this, a 60-min slot at 23:00 never fits (23:00+60 > 23:59).
   */
  private effectiveCloseMinutes(closeMinutes: number): number {
    if (closeMinutes >= 23 * 60 + 59) {
      return 24 * 60;
    }
    return closeMinutes;
  }

  private minutesToTime(totalMinutes: number): string {
    const dayMinutes = 24 * 60;
    const normalized = ((totalMinutes % dayMinutes) + dayMinutes) % dayMinutes;
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private formatAmPmFromMinutes(totalMinutes: number): string {
    const dayMinutes = 24 * 60;
    const normalized = ((totalMinutes % dayMinutes) + dayMinutes) % dayMinutes;
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayHour = ((hours + 11) % 12) + 1;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${suffix}`;
  }
}
